/*
  Bundle-vælgeren på produktsiden.

  Elementet holder selv styr på hvad kunden har valgt og lægger hele pakken i
  kurven i ét kald til /cart/add.js. Den går uden om produktformularen med
  vilje: formularen er bundet til ét enkelt produkt, og en pakke er flere.

  Priserne der vises kommer fra blokkens indstillinger. Rabatten kommer fra
  Shopify. De to skal holdes i sync manuelt — se kommentaren i snippet'en.
*/
if (!customElements.get('ahlam-bundle-builder')) {
  customElements.define(
    'ahlam-bundle-builder',
    class AhlamBundleBuilder extends HTMLElement {
      connectedCallback() {
        this.maxItems = parseInt(this.dataset.maxItems, 10) || 4;
        this.selection = new Map();

        this.panel = this.querySelector('[data-ahlam-bundle-panel]');
        this.list = this.querySelector('[data-ahlam-bundle-list]');
        this.items = Array.from(this.querySelectorAll('[data-ahlam-bundle-item]'));
        this.searchInput = this.querySelector('[data-ahlam-bundle-search]');
        this.chips = Array.from(this.querySelectorAll('[data-ahlam-bundle-chip]'));
        this.tiers = Array.from(this.querySelectorAll('[data-ahlam-bundle-tier]'));
        this.summary = this.querySelector('[data-ahlam-bundle-summary]');
        this.total = this.querySelector('[data-ahlam-bundle-total]');
        this.submit = this.querySelector('[data-ahlam-bundle-submit]');
        this.submitText = this.querySelector('[data-ahlam-bundle-submit-text]');
        this.errorEl = this.querySelector('[data-ahlam-bundle-error]');
        this.noResults = this.querySelector('[data-ahlam-bundle-noresults]');

        this.submitLabel = this.submitText ? this.submitText.textContent : '';
        this.priceTiers = this.readTiers();
        this.activeChip = '';

        this.bindModes();
        this.bindItems();
        this.bindFilters();
        if (this.submit) this.submit.addEventListener('click', this.addToCart.bind(this));

        this.render();
      }

      readTiers() {
        const node = this.querySelector('[data-ahlam-bundle-tiers]');
        if (!node) return [];
        try {
          // Højeste antal først, så opslaget kan tage det første match.
          return JSON.parse(node.textContent).sort((a, b) => b.qty - a.qty);
        } catch (e) {
          return [];
        }
      }

      bindModes() {
        this.querySelectorAll('[data-ahlam-bundle-mode]').forEach((input) => {
          input.addEventListener('change', () => {
            const bundle = input.value === 'bundle' && input.checked;
            if (this.panel) this.panel.hidden = !bundle;
            this.classList.toggle('ahlam-bundle--open', bundle);
          });
        });
      }

      bindItems() {
        this.items.forEach((item) => {
          const id = item.dataset.variantId;
          item.querySelectorAll('[data-ahlam-bundle-plus]').forEach((btn) => {
            btn.addEventListener('click', () => this.change(id, 1));
          });
          const minus = item.querySelector('[data-ahlam-bundle-minus]');
          if (minus) minus.addEventListener('click', () => this.change(id, -1));
        });
      }

      bindFilters() {
        if (this.searchInput) {
          this.searchInput.addEventListener('input', () => this.filter());
        }
        this.chips.forEach((chip) => {
          chip.addEventListener('click', () => {
            this.activeChip = chip.dataset.ahlamBundleChip || '';
            this.chips.forEach((c) => c.classList.toggle('is-active', c === chip));
            this.filter();
          });
        });
      }

      get count() {
        let n = 0;
        this.selection.forEach((qty) => {
          n += qty;
        });
        return n;
      }

      change(variantId, delta) {
        const current = this.selection.get(variantId) || 0;

        // Loftet gælder pakken som helhed, ikke den enkelte duft.
        if (delta > 0 && this.count >= this.maxItems) {
          this.flashLimit();
          return;
        }

        const next = Math.max(0, current + delta);
        if (next === 0) {
          this.selection.delete(variantId);
        } else {
          this.selection.set(variantId, next);
        }
        this.clearError();
        this.render();
      }

      flashLimit() {
        this.classList.add('ahlam-bundle--at-limit');
        window.clearTimeout(this._limitTimer);
        this._limitTimer = window.setTimeout(() => {
          this.classList.remove('ahlam-bundle--at-limit');
        }, 600);
      }

      unitPrice(count) {
        if (!this.priceTiers.length) return 0;
        const match = this.priceTiers.find((t) => count >= t.qty);
        // Under laveste trin gælder laveste trins pris (typisk 1 stk.).
        return match ? match.unit : this.priceTiers[this.priceTiers.length - 1].unit;
      }

      formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents);
        }
        const value = (cents / 100).toFixed(2).replace('.', ',');
        return value + ' ' + (this.dataset.currency || '');
      }

      render() {
        const count = this.count;

        this.items.forEach((item) => {
          const qty = this.selection.get(item.dataset.variantId) || 0;
          const countEl = item.querySelector('[data-ahlam-bundle-count]');
          if (countEl) countEl.textContent = qty;
          item.classList.toggle('is-selected', qty > 0);
          const minus = item.querySelector('[data-ahlam-bundle-minus]');
          if (minus) minus.disabled = qty === 0;
          const plus = item.querySelector('.ahlam-bundle__step--plus');
          if (plus) plus.disabled = count >= this.maxItems;
        });

        this.tiers.forEach((tier) => {
          const qty = parseInt(tier.dataset.ahlamBundleTier, 10);
          const isTop = tier === this.tiers[this.tiers.length - 1];
          const active = isTop ? count >= qty : count === qty;
          tier.classList.toggle('is-active', active);
        });

        if (this.summary) {
          this.summary.textContent = count + ' / ' + this.maxItems;
        }
        if (this.total) {
          this.total.textContent = count > 0 ? this.formatMoney(this.unitPrice(count) * count) : '';
        }
        if (this.submit) {
          this.submit.disabled = count === 0;
        }
      }

      filter() {
        const term = (this.searchInput ? this.searchInput.value : '').trim().toLowerCase();
        const chip = this.activeChip;
        let visible = 0;

        this.items.forEach((item) => {
          const matchesTerm = !term || item.dataset.title.indexOf(term) !== -1;
          const matchesChip = !chip || (' ' + item.dataset.tags + ' ').indexOf(' ' + chip + ' ') !== -1;
          const show = matchesTerm && matchesChip;
          item.hidden = !show;
          if (show) visible += 1;
        });

        if (this.noResults) this.noResults.hidden = visible > 0;
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (this.errorEl) this.errorEl.hidden = true;
      }

      async addToCart() {
        if (this.count === 0) return;

        const items = [];
        this.selection.forEach((quantity, id) => {
          items.push({ id: Number(id), quantity: quantity });
        });

        this.submit.disabled = true;
        this.classList.add('ahlam-bundle--loading');
        if (this.submitText) this.submitText.textContent = this.submitText.dataset.loading || this.submitLabel;

        // Samme mål som temaets egen product-form.js: enten kurv-notifikationen
        // eller kurv-skuffen, alt efter hvad butikken er sat op med.
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        try {
          const body = { items: items };
          if (cart && typeof cart.getSectionsToRender === 'function') {
            body.sections = cart.getSectionsToRender().map((s) => s.id);
            body.sections_url = window.location.pathname;
            if (typeof cart.setActiveElement === 'function') {
              cart.setActiveElement(document.activeElement);
            }
          }

          const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify(body),
          });

          const data = await response.json();
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Kunne ikke lægge pakken i kurven.');
          }

          this.selection.clear();
          this.render();

          // is-empty SKAL af, før skuffen tegnes om. Er den sat, mangler
          // #CartDrawer-Overlay i markup'en, og renderContents falder over den.
          if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');

          if (cart && data.sections) {
            cart.renderContents(data);
          } else {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
        } catch (error) {
          this.showError(error.message);
          this.submit.disabled = false;
        } finally {
          this.classList.remove('ahlam-bundle--loading');
          if (this.submitText) this.submitText.textContent = this.submitLabel;
          this.render();
        }
      }
    }
  );
}
